import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { BookOpen, Shirt, Sparkles, ArrowRight } from "lucide-react";

// When WooCommerce / Shopify is connected, update these URLs (or replace
// with internal /shop/books, /shop/apparel, /shop/resources routes).
const SHOP_LINKS = {
  books: "https://7pillarsoflife.com/shop/books",
  apparel: "https://7pillarsoflife.com/shop/apparel",
  resources: "https://7pillarsoflife.com/shop/resources",
};

const PAGE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/aeae3fbc16d5d58644a3f7af64679b3b75d25173e56b234801f6f4b79de53aeb.png";

const categories = [
  {
    key: "books",
    icon: BookOpen,
    title: "Books",
    desc: "Reflective reads, workbooks, and guided journals to support growth between sessions.",
    cta: "Shop Books",
    href: SHOP_LINKS.books,
    image: "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=900&q=80",
  },
  {
    key: "apparel",
    icon: Shirt,
    title: "Apparel",
    desc: "Comfortable, conversation-starting pieces inspired by the seven pillars — for everyday wear and gentle reminders.",
    cta: "Shop Apparel",
    href: SHOP_LINKS.apparel,
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=900&q=80",
  },
  {
    key: "resources",
    icon: Sparkles,
    title: "Resources",
    desc: "Digital guides, printable worksheets, and supportive tools designed to encourage reflection and everyday wellness.",
    cta: "View Resources",
    href: SHOP_LINKS.resources,
    image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=80",
  },
];

export default function Shop() {
  return (
    <div data-testid="shop-page">
      <SEO
        title="Shop Seven Pillars — Books, Apparel & Resources"
        description="Explore books, apparel, and supportive resources from Seven Pillars of Life — designed to encourage growth, reflection, and everyday wellness."
        path="/shop"
      />
      <PageHeader
        overline="Shop Seven Pillars"
        title={<>Books, Apparel &amp; <em className="italic text-terracotta">Resources.</em></>}
        subtitle="Explore books, apparel, and supportive resources designed to encourage growth, reflection, and everyday wellness."
        image={PAGE_IMG}
      />

      <section className="py-20 md:py-28 bg-bone" data-testid="shop-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-8">
            {categories.map((c) => {
              const Icon = c.icon;
              return (
                <article
                  key={c.key}
                  data-testid={`shop-category-${c.key}`}
                  className="group bg-surface border border-sage/25 rounded-2xl overflow-hidden hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="aspect-[5/3] overflow-hidden border-b border-sage/20">
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col flex-1">
                    <div className="h-11 w-11 rounded-full bg-forest/5 flex items-center justify-center text-forest mb-5">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-forest font-medium mb-3">{c.title}</h3>
                    <p className="text-ink/70 leading-relaxed mb-7">{c.desc}</p>
                    <a
                      href={c.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`shop-${c.key}-btn`}
                      className="mt-auto inline-flex items-center gap-2 bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-6 py-3 text-sm font-medium transition-colors w-fit"
                    >
                      {c.cta} <ArrowRight size={14} />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-16 max-w-3xl mx-auto text-center">
            <p className="text-sm text-ink/60 italic">
              Products are managed through our WooCommerce shop. New items are added throughout the year — follow us on social to be the first to know.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
